import * as moment from 'moment';
import { Address, mapAddress } from '.';

export enum PaymentMethodType {
  CreditCard = 'credit',
  BankAccount = 'check',
  HealthSavingsAccount = 'hsa',
}

export enum BankAccountType {
  Savings = 'savings',
  Checking = 'checking',
}

export enum CreditCardProcessorName {
  Visa = 'Visa',
  Mastercard = 'Mastercard',
  AmericanExpress = 'American Express',
  Discover = 'Discover',
}

// API Models

export interface ApiPaymentMethodAddress {
  address1: string;
  address2?: string;
  city: string;
  state: string; // State abbreviation is used here, e.g., NY
  zipCode: string;
  country: 'US';
}

interface ApiCreditCardDetails {
  type: PaymentMethodType.CreditCard;
  number: string;
  cvv: string;
  expirationMonth: string; // Two digits
  expirationYear: string; // Four digits
  processor: CreditCardProcessorName;
  healthAccount: boolean;
}

interface ApiBankAccountDetails {
  type: PaymentMethodType.BankAccount;
  accountNumber: string;
  routingNumber: string;
  accountType: BankAccountType;
}

type ApiPaymentMethodDetails = ApiCreditCardDetails | ApiBankAccountDetails;

export interface ApiCreatePaymentMethodRequest {
  default: boolean;
  firstName: string;
  lastName: string;
  currency: 'USD'; // Default should be 'USD'
  address: ApiPaymentMethodAddress;
  method: ApiPaymentMethodDetails;
}

export interface ApiCreateCreditCardPaymentMethodRequest extends ApiCreatePaymentMethodRequest {
  method: ApiCreditCardDetails;
}

export interface ApiCreateBankAccountPaymentMethodRequest extends ApiCreatePaymentMethodRequest {
  method: ApiBankAccountDetails;
}

export interface ApiPaymentMethodResponse extends ApiCreatePaymentMethodRequest {
  id: string;
  active: boolean;
}

export interface ApiEditPaymentMethodRequest {
  default: boolean;
}

export interface ApiInternalEditPaymentMethod {
  method: {
    expirationYear: string;
  };
}

// Front End Models

export interface CreditCard {
  number: string;
  expirationMonth: string; // Two digits
  expirationYear: string; // Four digits
  processor: CreditCardProcessorName;
  isHealthAccount: boolean;
  // Computed
  isExpired: boolean;
}

export interface BankAccount {
  accountNumber: string;
  routingNumber: string;
  accountType: BankAccountType;
}

interface PaymentMethodBase {
  id: string;
  isDefault: boolean;
  address: Address;
  type: PaymentMethodType.CreditCard | PaymentMethodType.BankAccount;
  info: CreditCard | BankAccount;
}

export interface CreditCardPaymentMethod extends PaymentMethodBase {
  type: PaymentMethodType.CreditCard;
  info: CreditCard;
}

export interface BankAccountPaymentMethod extends PaymentMethodBase {
  type: PaymentMethodType.BankAccount;
  info: BankAccount;
}

export type PaymentMethod = CreditCardPaymentMethod | BankAccountPaymentMethod;

// Form Models

export interface CreditCardFormValues {
  number: string;
  processor: CreditCardProcessorName;
  expirationMonth: string; // Two digits
  expirationYear: string; // Four digits
  cvv: string; // 3 or 4 digits as string
  isHealthAccount: boolean;
}

export interface BankAccountFormValues {
  accountNumber: string;
  routingNumber: string;
  accountType: BankAccountType;
}

export interface NewPaymentMethodFormValues {
  firstName: string;
  lastName: string;
  isDefault: boolean;
  addressPaymentMethodId: string | null; // Reference to the payment method containing the chosen address
  addressLine1: string | null;
  addressLine2: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZipCode: string | null;
  type: PaymentMethodType;
  creditCardInfo: CreditCardFormValues;
  bankAccountInfo: BankAccountFormValues;
}

// Mappers
export const mapPaymentMethod = (pm: ApiPaymentMethodResponse): PaymentMethod => {
  const paymentMethod = {
    id: pm.id,
    isDefault: pm.default,
    address: mapAddress(
      null,
      pm.address.address1,
      pm.address.address2,
      pm.address.city,
      pm.address.state,
      pm.address.zipCode,
      null
    )!,
    type: pm.method.type,
  };

  if (pm.method.type === PaymentMethodType.CreditCard) {
    const { expirationYear, expirationMonth, healthAccount } = pm.method;
    let isExpired = false;

    if (!!expirationYear && !!expirationMonth) {
      const today = moment().startOf('day');

      isExpired =
        (parseInt(expirationYear) === today.year() && parseInt(expirationMonth) < today.month() + 1) ||
        parseInt(expirationYear) < today.year();
    }

    const info = {
      number: pm.method.number,
      processor: pm.method.processor,
      expirationMonth: pm.method.expirationMonth, // Two digits
      expirationYear: pm.method.expirationYear, // Four digits
      cvv: pm.method.cvv, // 3 or 4 digits as string
      isExpired,
      isHealthAccount: pm.method.healthAccount,
    } as CreditCard;

    return {
      ...paymentMethod,
      info,
    } as CreditCardPaymentMethod;
  } else {
    const info = {
      accountNumber: pm.method.accountNumber,
      routingNumber: pm.method.routingNumber,
      accountType: pm.method.accountType,
    } as BankAccount;

    return {
      ...paymentMethod,
      info,
    } as BankAccountPaymentMethod;
  }
};

export const mapPaymentMethods = (paymentMethods: ApiPaymentMethodResponse[]): PaymentMethod[] => {
  return paymentMethods.filter((pm) => pm.active).map(mapPaymentMethod);
};

export const buildCreatePaymentMethodRequest = (
  form: NewPaymentMethodFormValues,
  paymentMethods: PaymentMethod[]
): ApiCreateCreditCardPaymentMethodRequest | ApiCreateBankAccountPaymentMethodRequest => {
  const mustSetToDefault = paymentMethods.length === 0;

  let address;

  if (form.addressPaymentMethodId) {
    const existingAddressPaymentMethod = paymentMethods.find((pm) => pm.id === form.addressPaymentMethodId)!;
    address = {
      address1: existingAddressPaymentMethod.address.line1!,
      address2: existingAddressPaymentMethod.address.line2 || undefined,
      city: existingAddressPaymentMethod.address.city!,
      state: existingAddressPaymentMethod.address.state!,
      zipCode: existingAddressPaymentMethod.address.zipCode!,
    };
  } else {
    address = {
      address1: form.addressLine1!,
      address2: form.addressLine2 || undefined,
      city: form.addressCity!,
      state: form.addressState!,
      zipCode: form.addressZipCode!,
    };
  }

  const paymentMethod = {
    default: mustSetToDefault || form.isDefault,
    firstName: form.firstName,
    lastName: form.lastName,
    currency: 'USD', // Default should be 'USD'
    address: {
      ...address,
      country: 'US',
    },
  };

  let method: ApiCreditCardDetails | ApiBankAccountDetails;

  if (form.type === PaymentMethodType.CreditCard || form.type === PaymentMethodType.HealthSavingsAccount) {
    const info = form.creditCardInfo;
    const isHSA = form.type === PaymentMethodType.HealthSavingsAccount;

    method = {
      type: PaymentMethodType.CreditCard,
      number: info.number,
      processor: info.processor,
      expirationMonth: info.expirationMonth, // Two digits
      expirationYear: info.expirationYear, // Four digits
      healthAccount: isHSA,
      cvv: info.cvv, // 3 or 4 digits submitted as string
    };

    return {
      ...paymentMethod,
      method,
    } as ApiCreateCreditCardPaymentMethodRequest;
  } else {
    const info = form.bankAccountInfo;

    method = {
      type: PaymentMethodType.BankAccount,
      accountNumber: info.accountNumber,
      routingNumber: info.routingNumber,
      accountType: info.accountType,
    };

    return {
      ...paymentMethod,
      method,
    } as ApiCreateBankAccountPaymentMethodRequest;
  }
};
